import React, { useEffect, useMemo, useRef, useState } from "react";
import FetchData from "../../providers/dataProvider";
import Spinner from "../common/Spinnner";
import useProductConfigurator from "../../hooks/useProductConfigurator";
import {
  ProductAttributeFilters,
  ProductModelPreview,
  ProductModelSearch,
  ProductSubCategoryTopbar,
} from "./ProductSubCategoryPanels";

import {
  ProductModelPreviewList,
  AssemblyDropdownFilters,
  FALLBACK_ATTRS,
  INITIAL_ASSEMBLY,
  DEFAULT_ASSEMBLY,
} from "./ProductModelPreviewList";

import "../../assets/styles/productsubcategory.css";
import "../../assets/styles/productsubcategory-multi.css";


const MULTI_CARD_GROUP_IDS = [16]; // Aluminium Louvers

// Assembly screen (groupId 19)
const ASSEMBLY_GROUP_ID = 19;
const FRESH_AIR_SOURCE_GROUP_ID = 16; // FAL245, KSTL
const DAMPER_SOURCE_GROUP_ID = 23; // JB11, JB12 ... (Steel Control Dampers)

const flattenProducts = (groups) =>
  (Array.isArray(groups) ? groups : [])
    .flatMap((g) => g.products ?? [])
    .filter((p) => p.isActive !== false);

const findByName = (groups, name) =>
  name
    ? flattenProducts(groups).find(
      (p) => p.productName?.toUpperCase() === name.toUpperCase()
    )
    : undefined;

export const ProductSubCategory = ({ setProductTab, ProductTab }) => {
  const [subProducts, setSubProducts] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [showDropdown, setShowDropdown] = useState(false);
  const [assembly, setAssembly] = useState(() =>
    Number(ProductTab?.productSubCategoryId) === ASSEMBLY_GROUP_ID
      ? ProductTab?.assemblySelection ?? DEFAULT_ASSEMBLY
      : INITIAL_ASSEMBLY
  );
  const searchWrapRef = useRef(null);

  const { data: productsubgroups, loading } = FetchData("productsubgroups", {
    groupId: ProductTab?.productSubCategoryId,
  });

  const isAssemblyGroup =
    Number(ProductTab?.productSubCategoryId) === ASSEMBLY_GROUP_ID;

  // Linked sources (only fetched on assembly screen)
  const { data: louverGroups } = FetchData("productsubgroups", {
    groupId: FRESH_AIR_SOURCE_GROUP_ID,
    skip: !isAssemblyGroup,
  });
  const { data: damperGroups } = FetchData("productsubgroups", {
    groupId: DAMPER_SOURCE_GROUP_ID,
    skip: !isAssemblyGroup,
  });

  useEffect(() => {
    if (productsubgroups?.length) {
      setSubProducts(productsubgroups);
    }
  }, [productsubgroups]);

  // reset dropdowns when user opens a different group
  useEffect(() => {
    setAssembly(
      Number(ProductTab?.productSubCategoryId) === ASSEMBLY_GROUP_ID
        ? ProductTab?.assemblySelection ?? DEFAULT_ASSEMBLY
        : INITIAL_ASSEMBLY
    );
  }, [ProductTab?.productSubCategoryId]);

  const {
    allColDefs,
    selections,
    matchedProduct,
    breadcrumbParts,
    hasAny,
    getFlatProducts,
    getVisibleColumnStates,
    handlePillClick,
    selectFromSearchResult,
    clearSelections,
    countMatches,
    getMatchingProducts,
  } = useProductConfigurator(subProducts);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchWrapRef.current && !searchWrapRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const flatProducts = useMemo(
    () => getFlatProducts(subProducts).filter((product) => product.isActive !== false),
    [getFlatProducts, subProducts]
  );

  const searchResults = useMemo(() => {
    const query = searchTerm.trim().toLowerCase();
    if (!query) return flatProducts;
    return flatProducts.filter((product) =>
      product.productName?.toLowerCase().includes(query)
    );
  }, [flatProducts, searchTerm]);

  const filterGroups = useMemo(
    () => getVisibleColumnStates(subProducts),
    [getVisibleColumnStates, subProducts]
  );

  const matchCount = hasAny ? countMatches(selections, subProducts) : flatProducts.length;

  const candidateProducts =
    hasAny && !matchedProduct ? getMatchingProducts(selections, subProducts) : [];

  const isLouverGroup = MULTI_CARD_GROUP_IDS.includes(
    Number(ProductTab?.productSubCategoryId)
  );

  const assemblyProduct = useMemo(
    () => (isAssemblyGroup ? flattenProducts(subProducts)[0] : undefined),
    [isAssemblyGroup, subProducts]
  );


  const previewProduct = matchedProduct || assemblyProduct;

  const assemblyAttributes = useMemo(() => {
    if (!isAssemblyGroup) return [];
    const product = assemblyProduct;
    const bySeq = (x, y) => (x.sequence ?? 0) - (y.sequence ?? 0);

    const attrs = (product?.productSpecGroupMaps ?? [])
      .flatMap((g) => g.productSpecMaps ?? [])
      .filter((s) => s.isSelectionParameter && s.isActive !== false)
      .sort(bySeq)
      .map((s) => {
        const values = (s.productSpecValueMaps ?? [])
          .filter((v) => v.isActive !== false)
          .sort(bySeq)
          .map((v) => v.valueName);
        if (s.attributeName === "Damper" && !values.includes("No")) values.push("No");
        return { name: s.attributeName, values };
      });

    return attrs.length ? attrs : FALLBACK_ATTRS;
  }, [isAssemblyGroup, assemblyProduct]);

  const assemblyLinkedProducts = useMemo(() => {
    if (!isAssemblyGroup) return [];
    const sources = {
      "Fresh Air Louver": louverGroups,
      "Sand Trap Louver": louverGroups,
      Damper: damperGroups,
    };
    return assemblyAttributes
      .map(({ name }) => findByName(sources[name], assembly.models[name]))
      .filter(Boolean);
  }, [isAssemblyGroup, louverGroups, damperGroups, assemblyAttributes, assembly.models]);

  const assemblyConfiguration = useMemo(
    () =>
      assemblyAttributes
        .filter(({ name }) => assembly.values[name])
        .map(({ name }) => ({
          attributeName: name,
          valueName: assembly.values[name],
        })),
    [assemblyAttributes, assembly.values]
  );

  const cardsInBody = isAssemblyGroup && assemblyLinkedProducts.length > 0;

  const hasAssemblySelection =
    JSON.stringify(assembly) !== JSON.stringify(DEFAULT_ASSEMBLY);

  const handleSearchFocus = () => {
    setShowDropdown(true);
  };

  const handleSearchResultClick = (product) => {
    selectFromSearchResult(product, subProducts);
    setSearchTerm(product.productName);
    setShowDropdown(false);
  };

  const handleClear = () => {
    clearSelections();
    setSearchTerm("");
    setShowDropdown(false);
    setAssembly(isAssemblyGroup ? DEFAULT_ASSEMBLY : INITIAL_ASSEMBLY);
  };

  const handleFilterClick = (col, value) => {
    handlePillClick(col, value, subProducts);
  };
  const handleConfigure = () => {
    const product = matchedProduct || assemblyProduct;
    if (!product) return;

    if (isAssemblyGroup) {
      if (assemblyLinkedProducts.length === 0) return;
      setProductTab({
        tab: "AssemblyConfigure",
        id: product,
        assemblyTabs: [
          ...assemblyLinkedProducts.map((p) => ({
            key: String(p.id),
            label: p.productName,
            product: p,
          })),
        ],
        selectedConfiguration: assemblyConfiguration,
        assemblyProduct: product,
        assemblySelection: assembly,
        subCategoryId: ProductTab?.subCategoryId,
        productSubCategoryId: ProductTab?.productSubCategoryId,
        title: ProductTab?.title,
        redirectTab: "ProductSubCategory",
      });
      return;
    }

    const selectedConfiguration = isAssemblyGroup
      ? assemblyConfiguration
      : allColDefs
        .filter((col) => selections[col.key])
        .map((col) => ({
          attributeName: col.attrName,
          valueName: selections[col.key],
        }));

    setProductTab({
      tab: "ProductQuotation",
      id: product,
      selectedConfiguration,
      subCategoryId: ProductTab?.subCategoryId,
      productSubCategoryId: ProductTab?.productSubCategoryId,
      title: ProductTab?.title,
      redirectTab: "ProductSubCategory",
      assemblySelection: ProductTab.assemblySelection,
    });
  };

  const handleConfigureCandidate = (product) => {
    const productAttributeNames = new Set(
      (product.productSpecGroupMaps ?? []).flatMap((group) =>
        (group.productSpecMaps ?? []).map((spec) => spec.attributeName)
      )
    );

    const selectedConfiguration = isAssemblyGroup
      ? assemblyConfiguration
      : allColDefs
        .filter((col) => selections[col.key] && productAttributeNames.has(col.attrName))
        .map((col) => ({
          attributeName: col.attrName,
          valueName: selections[col.key],
        }));

    setProductTab({
      tab: "ProductQuotation",
      id: product,
      selectedConfiguration,
      subCategoryId: ProductTab?.subCategoryId,
      productSubCategoryId: ProductTab?.productSubCategoryId,
      title: ProductTab?.title,
      redirectTab: "ProductSubCategory",
      assemblySelection: ProductTab.assemblySelection,
    });
  };

  const handleBack = () => {
    setProductTab({ tab: "SubCategory", subCategoryId: ProductTab?.subCategoryId });
  };

  return (
    <div className="psc-page">
      <ProductSubCategoryTopbar title={ProductTab?.title} onBack={handleBack} />

      <div className="row psc-split-row">
        <div
          className={`col-xxl-9 col-xl-9 col-lg-12 col-md-12 col-sm-12 col-xs-12 psc-left-col${cardsInBody ? " psc-left-col--cards" : ""
            }`}
        >
          {isAssemblyGroup ? (
            hasAssemblySelection && (
              <div className="asm-clear-row">
                <button type="button" className="psc-clear-btn" onClick={handleClear}>
                  Clear all
                </button>
              </div>
            )
          ) : (
            <ProductModelSearch
              searchTerm={searchTerm}
              searchResults={searchResults}
              showDropdown={showDropdown}
              hasSelection={hasAny}
              searchWrapRef={searchWrapRef}
              onSearchTermChange={(value) => {
                setSearchTerm(value);
                setShowDropdown(true);
              }}
              onFocus={handleSearchFocus}
              onClear={handleClear}
              onResultClick={handleSearchResultClick}
            />
          )}

          {isAssemblyGroup ? (
            <AssemblyDropdownFilters
              attributes={assemblyAttributes}
              value={assembly}
              onChange={setAssembly}
            />
          ) : (
            <ProductAttributeFilters
              filterGroups={filterGroups}
              onPillClick={handleFilterClick}
            />
          )}

        </div>

        <div className="col-xxl-3 col-xl-3 col-lg-12 col-md-12 col-sm-12 col-xs-12 psc-right-col">
          <div className="psc-preview-col">
            {isLouverGroup && !matchedProduct && candidateProducts.length > 0 ? (
              <ProductModelPreviewList
                products={candidateProducts}
                onConfigureProduct={handleConfigureCandidate}
              />
            ) : (
              <ProductModelPreview
                matchedProduct={previewProduct}
                breadcrumbParts={breadcrumbParts}
                hasSelection={hasAny || !!assemblyProduct}
                matchCount={matchCount}
                candidateProducts={candidateProducts}
                onConfigure={handleConfigure}
                onCandidateClick={handleSearchResultClick}
                highlightValues={isAssemblyGroup ? assembly.values : undefined}
              />
            )}
          </div>
        </div>
      </div>

      {loading && (
        <div className="psc-spinner-wrap">
          <Spinner />
        </div>
      )}
    </div>
  );
};